import { useCallback, useState } from 'react';
import { v4 as uuid4 } from 'uuid';
import { availableSearchSources, SearchResponseStatus, SearchType } from 'fym-common/src/types/searchService';
import { InspectType } from 'fym-common/src/types/inspectService';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { useLazyInspectByUrlQuery, useLazySearchQuery } from '../../../redux/api/musicAPI';
import { ReleaseLinkData } from '../wizards/releaseLink/types';
import {
  parseInspectResultToReleaseLinkData,
  parseSearchResults,
  parseUrlToReleaseLinkData,
} from '../wizards/releaseLink/utils';

export type PresaveReleaseUrlItem = {
  id: string;
  source?: MusicAPIIntegrationID;
  url: string;
};

function toItem(releaseLink: ReleaseLinkData): PresaveReleaseUrlItem {
  return { id: releaseLink.id, source: releaseLink.source, url: releaseLink.data.url ?? '' };
}

/**
 * Turns one pasted link into the whole release.
 *
 * The same two calls the Release Link wizard makes: musicapi says what the
 * link is, then searches every other service for the same track. The artist
 * edits what comes back — a match can be wrong, and on release day a wrong
 * link is worse than a missing one.
 */
export function usePresaveReleaseUrls() {
  const [inspectByUrl, { isLoading: isInspectLoading, error: inspectError }] = useLazyInspectByUrlQuery();
  const [search, { isLoading: isSearchLoading, error: searchError }] = useLazySearchQuery();
  const [items, setItems] = useState<PresaveReleaseUrlItem[]>([]);

  const prefill = useCallback(
    async (url: string): Promise<void> => {
      const inspectResult = await inspectByUrl(url);
      if (!inspectResult.isSuccess) {
        return;
      }
      let mainLink: ReleaseLinkData | undefined;
      if (inspectResult.data.type === InspectType.unknown) {
        mainLink = parseUrlToReleaseLinkData(url);
      } else if (inspectResult.data.status === SearchResponseStatus.success) {
        mainLink = parseInspectResultToReleaseLinkData(inspectResult.data);
      }
      if (!mainLink) {
        return;
      }

      // An unrecognised link has no track name to search other services with.
      // It still goes on the list: the server may yet identify it.
      if (mainLink.type === SearchType.unknown) {
        setItems([toItem(mainLink)]);
        return;
      }

      const searchResult = await search({
        track: mainLink.data.track ?? undefined,
        album: mainLink.data.album ?? undefined,
        artist: mainLink.data.artist ?? undefined,
        sources: availableSearchSources.filter((source) => source !== mainLink?.source),
        type: mainLink.type,
      });
      if (!searchResult.isSuccess) {
        setItems([toItem(mainLink)]);
        return;
      }
      const { tracks, albums } = searchResult.data;
      const matches = parseSearchResults([...(tracks ?? []), ...(albums ?? [])]);
      setItems([toItem(mainLink), ...matches.map(toItem)]);
    },
    [inspectByUrl, search]
  );

  const changeUrl = useCallback((id: string, url: string) => {
    setItems((previous) => previous.map((item) => (item.id === id ? { ...item, url } : item)));
  }, []);

  const removeUrl = useCallback((id: string) => {
    setItems((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const addUrl = useCallback(() => {
    setItems((previous) => [...previous, { id: uuid4(), url: '' }]);
  }, []);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  return {
    items,
    prefill,
    changeUrl,
    removeUrl,
    addUrl,
    clear,
    isLoading: isInspectLoading || isSearchLoading,
    error: inspectError ?? searchError,
  };
}
